// Settings › Campaigns (BL-008): the words and order for campaigns and the tracking numbers that
// credit calls to them. The screen holds no rules of its own: the server decides what a call is
// credited to.

export const CHANNELS = [
  'google_ads',
  'microsoft_ads',
  'lsa',
  'web',
  'referral',
  'vehicle',
  'print',
  'other',
] as const;
export type Channel = (typeof CHANNELS)[number];

export const CHANNEL_WORDS: Record<Channel, string> = {
  google_ads: 'Google Ads',
  microsoft_ads: 'Microsoft Ads (Bing)',
  lsa: 'Local Services',
  web: 'Website',
  referral: 'Referral',
  vehicle: 'Vehicles',
  print: 'Print and mail',
  other: 'Other',
};

export interface CampaignRef {
  id: string;
  name: string;
  channel: Channel;
}
export interface Campaign extends CampaignRef {
  active: boolean;
  fromServiceTitan: boolean;
  numbers: { phone: string; text: string; label: string | null }[];
  rowVersion: number;
}
export interface TrackingNumber {
  phone: string;
  text: string;
  label: string | null;
  campaign: CampaignRef | null;
  /** Where its calls ring through to (BL-001); left out when it has none. */
  forwardTo?: string;
  forwardText?: string;
}

/** "Google Ads · 2 numbers", "Vehicles · no number yet", "Print and mail · retired". */
export function campaignMeans(c: Campaign): string {
  const parts = [CHANNEL_WORDS[c.channel]];
  if (!c.active) parts.push('retired');
  else
    parts.push(
      c.numbers.length ? `${c.numbers.length} number${c.numbers.length === 1 ? '' : 's'}` : 'no number yet',
    );
  if (c.fromServiceTitan) parts.push('from ServiceTitan');
  return parts.join(' · ');
}

/** Where a number's calls go, in words: "Calls are credited to Local Services", or why none. */
export function numberMeans(n: TrackingNumber): string {
  return n.campaign ? `Calls are credited to ${n.campaign.name}` : 'Calls aren’t credited to a campaign';
}

/** Live campaigns first, by name; retired ones after. */
export function campaignOrder(list: readonly Campaign[]): Campaign[] {
  return [...list].sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
}

/** Where a number's calls ring once it points at Flowology (BL-001), or null when the main line takes them. */
export function forwardMeans(n: TrackingNumber): string | null {
  return n.forwardText ? `Rings through to ${n.forwardText}` : null;
}

/** The phone webhook's settings for calls nobody answers (calls.mainLine, .ringSeconds, .recordForwarded). */
export interface Unanswered {
  mainLine: string | null;
  ringSeconds: number;
  record: boolean;
}

/** What happens to a call nobody answers, in words: the main line next (or straight to voicemail when
 * there's none), the ring time, and whether answered calls are recorded. */
export function unansweredMeans(u: Unanswered, text: (e164: string) => string): string {
  const ring = u.mainLine
    ? `A number with no forward-to, or one not answered, rings the main line, ${text(u.mainLine)}. Not answered there within ${u.ringSeconds} seconds, the caller leaves a voicemail.`
    : `No main line: a number with no forward-to, or one not answered within ${u.ringSeconds} seconds, takes a voicemail.`;
  return `${ring} ${u.record ? 'Answered calls are recorded.' : 'Answered calls aren’t recorded.'}`;
}

export interface BlockedNumber {
  phone: string;
  text: string;
  note: string | null;
  blockedAt: string;
}

/** "3 numbers blocked.", or that none are. */
export function blockedCount(total: number): string {
  return total ? `${total} ${total === 1 ? 'number' : 'numbers'} blocked.` : 'No numbers blocked.';
}
